import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { FloatLabelType } from '@angular/material/form-field';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MunilyFilterFieldSize } from '../filter-fields/filter-field.types';
import {
  MunilySelectFieldOption,
  MunilyTextFieldErrorMessages,
  MunilyTextFieldWidthLayout,
} from '../select-field/select-field.types';
import { MUNILY_FILTER_SELECT_FIELD_DEFAULTS } from './filter-select-field.types';

/**
 * Select para **filtros** (toolbar). Siempre `appearance="outline"`.
 * Opcional: limpiar selección (`clearable`) con icono y fondo activo (DS / Figma 5176-6553).
 */
@Component({
  selector: 'app-filter-select-field',
  templateUrl: './filter-select-field.component.html',
  styleUrls: ['./filter-select-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterSelectFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control?: FormControl;

  @Input() size: MunilyFilterFieldSize = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.size;
  @Input() widthLayout: MunilyTextFieldWidthLayout = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.widthLayout;
  @Input() maxWidth?: string;

  @Input() options: MunilySelectFieldOption[] = [];
  @Input() multiple = false;

  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() iconLeading?: string;

  /** Clase del overlay; por defecto estilos solo para panel de filtro (no `custom-select-panel`). */
  @Input() panelClass = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.panelClass;

  @Input() disableOptionCentering = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.disableOptionCentering;
  @Input() compareWith?: (a: unknown, b: unknown) => boolean;

  /** MatSelect requires a function; `undefined` throws at runtime. */
  public readonly defaultCompareWith = (a: unknown, b: unknown): boolean => a === b;

  get resolvedCompareWith(): (a: unknown, b: unknown) => boolean {
    return this.compareWith ?? this.defaultCompareWith;
  }

  /** Muestra icono para vaciar la selección (single). Usar con `clearValue` si el “vacío” no es `null`. */
  @Input() clearable = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.clearable;

  /** Valor al limpiar (p. ej. `''` si la primera opción es “Todos”). Si no se define, se usa `null`. */
  @Input() clearValue?: unknown;

  @Input() clearAriaLabel = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.clearAriaLabel;

  /** Muestra un input en el panel para filtrar opciones por texto. */
  @Input() searchable = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.searchable;

  @Input() searchPlaceholder = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.searchPlaceholder;

  @Input() floatLabel: FloatLabelType = MUNILY_FILTER_SELECT_FIELD_DEFAULTS.floatLabel;
  @Input() disabled = false;
  @Input() successful = false;

  @Input() errorMessage = '';
  @Input() errorMessages: MunilyTextFieldErrorMessages = {};

  @Output() selectionChange = new EventEmitter<MatSelectChange>();
  @Output() valueChange = new EventEmitter<unknown>();
  /** Emite cuando el usuario limpia con el icono (valor resultante = `clearValue` o `null`). */
  @Output() cleared = new EventEmitter<void>();

  @ViewChild('optionSearchInput') optionSearchInput?: ElementRef<HTMLInputElement>;

  public formFieldClasses: string[] = [];
  public readonly optionFilterControl = new FormControl('');

  @HostBinding('class.munily-ff-host--ds-default')
  get hostDsDefaultMaxWidth(): boolean {
    return this.widthLayout === 'ds' && !this.maxWidth;
  }

  @HostBinding('style.max-width')
  get hostMaxWidthStyle(): string | null {
    if (this.widthLayout === 'fill') {
      return 'none';
    }
    if (this.widthLayout === 'ds' && this.maxWidth) {
      return this.maxWidth;
    }
    return null;
  }

  readonly fallbackControl = new FormControl(null);
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
  ) {}

  get resolvedControl(): FormControl {
    return this.control ?? this.fallbackControl;
  }

  get filteredOptions(): MunilySelectFieldOption[] {
    if (!this.searchable) {
      return this.options;
    }
    const term = String(this.optionFilterControl.value ?? '').trim().toLowerCase();
    if (!term) {
      return this.options;
    }
    return this.options.filter((opt) => this.optionMatchesSearch(opt, term));
  }

  get resolvedErrorMessage(): string {
    if (this.errorMessage) return this.errorMessage;
    const errors = this.resolvedControl.errors;
    if (!errors) return '';
    return this.firstErrorMessage(errors);
  }

  ngOnInit(): void {
    this.syncDisabled();
    this.wireControlUpdates();
    this.syncFormFieldClasses();
    this.optionFilterControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.syncDisabled();
    }
    if (changes['control']) {
      this.destroy$.next();
      this.syncDisabled();
      this.wireControlUpdates();
    }
    if (
      changes['size'] ||
      changes['successful'] ||
      changes['disabled'] ||
      changes['control'] ||
      changes['clearable'] ||
      changes['multiple'] ||
      changes['clearValue']
    ) {
      this.syncFormFieldClasses();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showError(): boolean {
    const c = this.resolvedControl;
    return c.invalid && (c.touched || c.dirty);
  }

  showSuccessOutline(): boolean {
    return this.successful && !this.showError();
  }

  onSelectionChange(ev: MatSelectChange): void {
    this.selectionChange.emit(ev);
    this.valueChange.emit(ev.value);
  }

  onOpenedChange(open: boolean): void {
    if (open && this.searchable) {
      this.optionFilterControl.setValue('', { emitEvent: false });
      setTimeout(() => this.optionSearchInput?.nativeElement?.focus());
    }
    if (!open) {
      this.optionFilterControl.setValue('', { emitEvent: false });
      this.resolvedControl.markAsTouched();
      this.syncFormFieldClasses();
      this.cdr.markForCheck();
    }
  }

  private optionMatchesSearch(opt: MunilySelectFieldOption, term: string): boolean {
    if (opt.label && opt.label.toLowerCase().includes(term)) {
      return true;
    }
    if (opt.labelKey) {
      const translated = String(this.translate.instant(opt.labelKey) ?? '').toLowerCase();
      if (translated.includes(term)) {
        return true;
      }
    }
    return String(opt.value ?? '').toLowerCase().includes(term);
  }

  /** Hay valor distinto del estado “vacío” y se puede mostrar el icono de limpiar. */
  showClearButton(): boolean {
    if (!this.clearable || this.disabled || this.multiple) {
      return false;
    }
    const current = this.resolvedControl.value;
    const empty = this.clearValue !== undefined ? this.clearValue : null;
    if (this.compareWith) {
      try {
        if (this.compareWith(current, empty)) {
          return false;
        }
      } catch {
        // continuar con comparación por ===
      }
    }
    if (current === empty) {
      return false;
    }
    if (empty === null && (current === null || current === undefined)) {
      return false;
    }
    return true;
  }

  onClear(): void {
    const reset = this.clearValue !== undefined ? this.clearValue : null;
    this.resolvedControl.setValue(reset);
    this.resolvedControl.markAsDirty();
    this.valueChange.emit(reset);
    this.cleared.emit();
    this.syncFormFieldClasses();
    this.cdr.markForCheck();
  }

  onClearPointerDown(event: MouseEvent): void {
    event.stopPropagation();
  }

  private syncFormFieldClasses(): void {
    const next: string[] = [];

    if (this.size === 'large') next.push('munily-filter-select-field--size-large');
    if (this.size === 'small') next.push('munily-filter-select-field--size-small');

    if (this.showClearButton()) next.push('munily-filter-select-field--has-value');
    if (this.showSuccessOutline()) next.push('munily-filter-select-field--success');
    if (this.showError()) next.push('mat-form-field-invalid');

    this.formFieldClasses = next;
  }

  private syncDisabled(): void {
    const ctrl = this.resolvedControl;
    if (this.disabled && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  private wireControlUpdates(): void {
    const ctrl = this.resolvedControl;
    ctrl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncFormFieldClasses();
      this.cdr.markForCheck();
    });
    ctrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncFormFieldClasses();
      this.cdr.markForCheck();
    });
  }

  private firstErrorMessage(errors: ValidationErrors): string {
    const key = Object.keys(errors)[0];
    const custom = this.errorMessages[key];
    if (custom) return custom;

    const err = errors[key];
    if (key === 'required') return 'Este campo es obligatorio';
    if (typeof err === 'object' && err?.message) return String(err.message);
    return 'Valor no válido';
  }
}
